import axios from 'axios';
import { basicAuthConfig } from '../configs';
import { urlMyspreadApi } from '../urls';

/**
 * API para gestión de Cierres de Conductores
 * Maneja la comunicación con el backend para obtener, crear y actualizar cierres
 */

/**
 * Obtener cierres paginados y filtrados
 * @param {Object} user - Usuario autenticado
 * @param {Object} params - { pageSize, pageNumber, fecha_inicio, fecha_termino, empresa_id, usuario_movil_id, searchValue }
 * @returns {Promise} Response con lista de cierres y total
 */
export const getCierres = async (user, params = {}) => {
  try {
    const config = basicAuthConfig(user);
    let endpoint = urlMyspreadApi.replace(/\/$/, '') + '/mantenedores/cierre/?';
    const query = [];
    
    if (params.pageSize) query.push(`pageSize=${params.pageSize}`);
    if (params.pageNumber) query.push(`pageNumber=${params.pageNumber}`);
    if (params.fecha_inicio) query.push(`fecha_inicio=${params.fecha_inicio}`);
    if (params.fecha_termino) query.push(`fecha_termino=${params.fecha_termino}`);
    if (params.empresa_id) query.push(`empresa_id=${params.empresa_id}`);
    if (params.usuario_movil_id) query.push(`usuario_movil_id=${params.usuario_movil_id}`);
    if (params.searchValue && params.searchValue.length > 0) {
      query.push(`searchValue=${encodeURIComponent(params.searchValue)}`);
    }
    
    endpoint += query.join('&');
    const response = await axios.get(endpoint, config);
    
    if (response.data) {
      return {
        success: true,
        data: response.data.data,
        total: response.data.total,
        pageNumber: response.data.pageNumber,
        pageSize: response.data.pageSize,
        totalPages: response.data.totalPages,
        message: response.data.message
      };
    }
    
    return {
      success: false,
      error: 'No se recibieron datos del servidor',
      data: [],
      total: 0
    };
  } catch (error) {
    console.error('Error al obtener cierres:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al obtener cierres',
      data: [],
      total: 0
    };
  }
};

/**
 * Crear nuevo cierre
 * @param {Object} user - Usuario autenticado
 * @param {Object} cierreData - Datos del cierre a crear
 * @returns {Promise} Response con resultado de la operación
 */
export const createCierre = async (user, cierreData) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/cierre/`;
    
    const response = await axios.post(endpoint, cierreData, config);
    
    return {
      success: true,
      data: response.data.data,
      message: response.data.message
    };
  } catch (error) {
    console.error('Error al crear cierre:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al crear cierre',
      details: error.response?.data
    };
  }
};

/**
 * Actualizar/Refresh cierre
 * @param {Object} user - Usuario autenticado
 * @param {number} cierreId - ID del cierre a actualizar
 * @returns {Promise} Response con resultado de la operación
 */
export const refreshCierre = async (user, cierreId) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/cierre/${cierreId}/refresh`;
    
    const response = await axios.put(endpoint, {}, config);
    
    return {
      success: true,
      data: response.data,
      message: response.data.message
    };
  } catch (error) {
    console.error('Error al actualizar cierre:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al actualizar cierre',
      details: error.response?.data
    };
  }
};

/**
 * Generar PDF desde Excel para un cierre específico
 * @param {Object} user - Usuario autenticado
 * @param {number} cierreId - ID del cierre para generar PDF
 * @returns {Promise} Response con resultado de la operación
 */
export const generatePdfFromExcel = async (user, cierreId) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/cierre/${cierreId}/generate-pdf-from-excel`;
    
    const response = await axios.post(endpoint, {}, config);
    
    return {
      success: true,
      data: response.data,
      message: response.data.message,
      process_id_pdf: response.data.process_id_pdf,
      cierre_id: response.data.cierre_id,
      optimization: response.data.optimization
    };
  } catch (error) {
    console.error('Error al generar PDF desde Excel:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al generar PDF desde Excel',
      details: error.response?.data
    };
  }
};

/**
 * Obtener resumen diario detallado por fecha específica
 * @param {Object} user - Usuario autenticado
 * @param {string} fecha - Fecha en formato YYYY-MM-DD
 * @returns {Promise} Response con resumen diario detallado
 */
export const getResumenDiario = async (user, fecha) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/cierre/resumen-diario/fecha/${fecha}?detallado=true`;
    
    const response = await axios.get(endpoint, config);
    
    return {
      success: true,
      data: response.data,
      message: 'Resumen diario obtenido exitosamente'
    };
  } catch (error) {
    console.error('Error al obtener resumen diario:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al obtener resumen diario',
      data: null
    };
  }
};

/**
 * Obtener resumen diario detallado por rango de fechas
 * @param {Object} user - Usuario autenticado
 * @param {string} fechaInicio - Fecha inicio en formato YYYY-MM-DD
 * @param {string} fechaFin - Fecha fin en formato YYYY-MM-DD
 * @returns {Promise} Response con resumen diario detallado
 */
export const getResumenDiarioRango = async (user, fechaInicio, fechaFin) => {
  try {
    const config = basicAuthConfig(user);
    let endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/cierre/resumen-diario/detallado?`;
    const query = [];
    
    if (fechaInicio) query.push(`fecha_inicio=${fechaInicio}`);
    if (fechaFin) query.push(`fecha_fin=${fechaFin}`);
    
    endpoint += query.join('&');
    const response = await axios.get(endpoint, config);
    
    return {
      success: true,
      data: response.data,
      message: 'Resumen diario obtenido exitosamente'
    };
  } catch (error) {
    console.error('Error al obtener resumen diario por rango:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al obtener resumen diario',
      data: null
    };
  }
};

/**
 * Obtener resumen diario detallado por rango de fechas
 * @param {Object} user - Usuario autenticado
 * @param {string} fechaInicio - Fecha inicio en formato YYYY-MM-DD
 * @param {string} fechaFin - Fecha fin en formato YYYY-MM-DD
 * @returns {Promise} Response con resumen diario detallado
 */
export const getResumenDiarioDetallado = async (user, fechaInicio, fechaFin) => {
  try {
    const config = basicAuthConfig(user);
    let endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/cierre/resumen-diario/detallado?`;
    const query = [];
    
    if (fechaInicio) query.push(`fecha_inicio=${fechaInicio}`);
    if (fechaFin) query.push(`fecha_fin=${fechaFin}`);
    
    endpoint += query.join('&');

    const response = await axios.get(endpoint, config);

    return {
      success: true,
      data: response.data,
      message: 'Resumen diario detallado obtenido exitosamente'
    };
  } catch (error) {
    console.error('Error al obtener resumen diario detallado:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al obtener resumen diario detallado',
      data: null
    };
  }
};

/**
 * Obtener detalle de un cierre específico por ID
 * @param {Object} user - Usuario autenticado
 * @param {number} cierreId - ID del cierre
 * @returns {Promise} Response con detalle del cierre
 */
export const getCierreById = async (user, cierreId) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/cierre/${cierreId}`;
    
    const response = await axios.get(endpoint, config);
    
    return {
      success: true,
      data: response.data,
      message: 'Detalle del cierre obtenido exitosamente'
    };
  } catch (error) {
    console.error('Error al obtener detalle del cierre:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al obtener detalle del cierre',
      data: null
    };
  }
};

/**
 * Enviar correos de un cierre específico
 * @param {Object} user - Usuario autenticado
 * @param {number} cierreId - ID del cierre
 * @param {Object} emailData - Datos para el envío de correos
 * @returns {Promise} Response con resultado del proceso
 */
export const sendCierreEmails = async (user, cierreId, emailData) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/cierre/${cierreId}/send-emails`;
    
    const response = await axios.post(endpoint, emailData, config);
    
    return {
      success: true,
      data: response.data,
      message: response.data.message,
      process_id_email: response.data.process_id_email,
      test_mode: response.data.test_mode
    };
  } catch (error) {
    console.error('Error al enviar correos del cierre:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al enviar correos del cierre',
      details: error.response?.data
    };
  }
};

/**
 * Buscar combinaciones de errores de tarifarios desde errores del cierre diario
 * @param {Object} user - Usuario autenticado
 * @param {Object} data - Datos de búsqueda { errores: [...] }
 * @returns {Promise} Response con las combinaciones encontradas
 */
export const searchCombinationsFromErrors = async (user, data) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = urlMyspreadApi.replace(/\/$/, '') + '/mantenedores/tarifarios/search-combinations-from-errors';
    const response = await axios.post(endpoint, data, config);

    if (response.data) {
      return {
        success: true,
        data: response.data.data || response.data,
        message: response.data.message
      };
    }

    return {
      success: false,
      error: 'No se recibieron datos del servidor',
      data: null
    };
  } catch (error) {
    console.error('Error al buscar combinaciones desde errores:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al buscar combinaciones desde errores',
      details: error.response?.data
    };
  }
};
